<template>
  <div
    :id="pendingSale.itemId"
    v-observe-visibility="visibilityChanged"
    :class="['pending-sale', { alert: hasAlert, warning: hasWarning }]"
  >
    <div :class="['wrapper', { visible }]">
      <section class="top">
        <align
          class="header"
          flex
          direction="flex-start"
          small-gap
        >
          <a class="link" :href="`/card/${pendingSale.cardId}?showSales=true`" target="_blank">
            <div><b>{{ pendingSale.cardLabel }}</b></div>
          </a>

          <external-link
            v-if="isEbaySale"
            href="https://130point.com/sales/"
            @click.native="$copyText(pendingSale.title)"
          >
            <span>130</span>
          </external-link>

          <external-link
            v-if="isEbaySale"
            :href="terapeakLink"
          >
            <span>Tera</span>
          </external-link>

          <external-link
            v-if="ebaySearchUrl"
            :href="ebaySearchUrl"
          >
            <span>eBay</span>
          </external-link>

          <external-link
            v-if="salesHistoryUrl"
            :href="salesHistoryUrl"
          >
            <span>Sales</span>
          </external-link>

          <external-link
            v-if="pendingSale.email"
            @click.native="sendEmail"
          >
            Email
          </external-link>
        </align>

        <align direction="flex-start">
          <div class="images-container">
            <a :href="saleUrl" target="_blank">
              <img
                v-if="pendingSale.image"
                v-lazy="{ src: pendingSale.image }"
                class="fade platform-image"
              />
            </a>

            <card-image v-if="pendingSale.cardId" :image-url="cardThumbnail" />
          </div>

          <div class="details-container">
            <a
              v-if="pendingSale.title"
              :href="saleUrl"
              class="link title"
              target="_blank"
            >
              <b>{{ pendingSale.title }}</b>
            </a>

            <div v-if="pendingSale.newestSaleDate">
              <span>Last Sale Date:</span> <b>{{ $filters.date(pendingSale.newestSaleDate) }}</b>
            </div>

            <div>
              <span>End Date:</span> <b>{{ $filters.datetime(pendingSale.date) }}</b>
            </div>

            <div v-if="pendingSale.listingType">
              <span>Type:</span> <b>{{ pendingSale.listingType }}</b> <b v-if="pendingSale.bids">({{ pendingSale.bids }} bids)</b>
            </div>

            <div v-if="platform">
              <span>Platform:</span> <b>{{ platform }}</b>
            </div>

            <div v-if="pendingSale.seller && pendingSale.feedback">
              <span>Seller:</span> <b>{{ pendingSale.seller }} ({{ pendingSale.feedback }})</b>
            </div>

            <div v-if="pendingSale.scrapeSuccess || pendingSale.scrapeFailed">
              <span>Best Offer Status: </span>
              <span v-if="pendingSale.scrapeSuccess" class="success">Success</span>
              <span v-if="pendingSale.scrapeFailed" class="error">Failed</span>
            </div>

            <div v-if="pendingSale.auctionEndedEarly" class="error">
              Auction Ended Early
            </div>

            <div v-if="attributes" class="success">
              {{ attributes }}
            </div>

            <graph
              v-if="sale.cardId && !hideGraph"
              class="pending-sales-graph xlarge-up"
              hide-controls
              scale="linear"
              :labels="labels"
              :datasets="datasets"
              :zoom="92"
            />

            <div v-if="Boolean(pendingSale.notes)" class="pending-sale-notes">
              <div><b>Seller Notes:</b></div>
              <div>{{ pendingSale.notes }}</div>
            </div>

            <div v-if="Boolean(pendingSale.employeeNotes)" class="pending-sale-employee-notes">
              <div><b>Employee Notes:</b></div>
              <div>{{ pendingSale.employeeNotes }}</div>
            </div>
          </div>
        </align>
      </section>

      <div v-if="pendingSale.marketValue">
        <span>Market Value:</span> <b>{{ $filters.currency(pendingSale.marketValue, { full: true }) }}</b>
      </div>

      <div v-if="pendingSale.listPrice">
        <span>List Price:</span> <b>{{ $filters.currency(pendingSale.listPrice, { full: true }) }}</b>
      </div>

      <br />

      <align small-gap>
        <form @submit.prevent="confirmPendingSale">
          <align small-gap>
            <currency-input
              :id="`price-${pendingSale.itemId}`"
              v-model="pendingSale.price"
              label="Price"
            />

            <btn kind="secondary" type="submit">
              Submit
            </btn>
            <icon v-if="pendingSale.verifiedCert" name="check_circle" />
            <icon-button v-if="pendingSale.switchedCardIdFromCert" icon="autorenew" @click="switchBack" />
            <icon v-if="pendingSale.noMatchingCert" name="close" />
          </align>
        </form>

        <align small-gap>
          <icon-button
            tooltip="Delete pending sale"
            icon="delete_outline"
            @click="deletePendingSale"
          />

          <icon-button
            tooltip="Send to purgatory"
            icon="assignment_late"
            @click="sendToPurgatory"
          />

          <icon-button
            tooltip="Flag sale for headlines"
            icon="flag"
            @click="flagForHeadlines"
          />

          <icon-button
            tooltip="Edit Pending Sale"
            class="edit"
            icon="edit"
            @click="editPendingSale"
          />

          <dropdown
            v-if="otherGradeOptions.length && condition"
            align="top-right"
            :active="otherGradesMenuOpen"
            @close="otherGradesMenuOpen = false"
          >
            <grade-variation-chip
              as="button"
              dropdown
              no-color
              :grade="condition"
              @click.native.stop="otherGradesMenuOpen = !otherGradesMenuOpen"
            />

            <template #menu>
              <dropdown-menu
                :options="otherGradeOptions"
                :max-height="200"
                @select="switchCard"
              />
            </template>
          </dropdown>

          <dropdown
            v-if="showVariationsDropdown"
            align="top-right"
            :active="otherVariationsMenuOpen"
            @close="otherVariationsMenuOpen = false"
          >
            <grade-variation-chip
              as="button"
              dropdown
              :variation="variation"
              @click.native.stop="otherVariationsMenuOpen = !otherVariationsMenuOpen"
            />

            <template #menu>
              <dropdown-menu
                :options="otherVariationOptions"
                :max-height="200"
                @select="switchCard"
              />
            </template>
          </dropdown>
        </align>
      </align>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import * as Sentry from '@sentry/vue';
import firebase from 'firebase/compat/app';

import { cardService, saleService } from '@/services';

import CardImage from '@cardladder/shared/components/CardImage';
import GradeVariationChip from '@cardladder/shared/components/GradeVariationChip';

import { toEbayUrl, sortOtherGrades } from '@cardladder/shared/utils/format.util';
import { getSalesGraphData } from '@cardladder/shared/utils/graphs.util';
import { toImageUrl, getTerapeakLink } from '@cardladder/shared/utils/format.util';
import { SELLING_PLATFORM_OPTIONS } from '@cardladder/shared/constants';

export default {
  components: {
    CardImage,
    GradeVariationChip,
  },

  props: {
    sale: Object,
    hideGraph: Boolean,
  },

  data() {
    return {
      loaded: false,
      visible: false,
      pendingSale: this.sale,
      labels: [],
      datasets: [],
      otherGrades: [],
      otherVariations: [],
      otherGradesMenuOpen: false,
      otherVariationsMenuOpen: false,
      condition: '',
      variation: '',
    };
  },

  watch: {
    sale: {
      handler(value) {
        this.pendingSale = {
          ...this.pendingSale,
          ...value,
        };
      },
      deep: true,
    },

    'pendingSale.price': {
      handler(price) {
        this.$bus.$emit('pending-sale-updated', {
          ...this.pendingSale,
          price,
        });
      },
    },
  },

  computed: {
    ...mapState({
      user: (state) => state.user,
    }),

    isEbaySale() {
      return this.pendingSale.platform === 'eBay';
    },

    ebaySearchUrl() {
      if (!this.pendingSale.eBayQuery) {
        return null;
      }

      return toEbayUrl(`/sch/i.html?_nkw=${encodeURIComponent(this.pendingSale.eBayQuery)}`);
    },

    salesHistoryUrl() {
      const query = this.pendingSale.eBayQuery || this.pendingSale.ebayQuery;

      if (!this.pendingSale.itemId || !query) {
        return null;
      }

      const rawItemId = String(this.pendingSale.itemId);
      const saleId = rawItemId.includes('ebay') ? rawItemId : `ebay-${rawItemId}`;

      return `/sales-history?saleId=${encodeURIComponent(saleId)}&q=${encodeURIComponent(query)}`;
    },

    hasAlert() {
      const hasPriceAlert = this.pendingSale?.status === 'priceJump';
      const hasSellerFeedbackAlert = this.pendingSale?.feedback && this.pendingSale?.feedback < 3;

      return hasPriceAlert || hasSellerFeedbackAlert;
    },

    hasWarning() {
      return this.pendingSale?.needsReview;
    },

    platform() {
      if (!this.pendingSale || !this.pendingSale.platform || !SELLING_PLATFORM_OPTIONS.find(({ value }) => value === this.pendingSale.platform)) {
        return null;
      }

      return SELLING_PLATFORM_OPTIONS.find(({ value }) => value === this.pendingSale.platform).label;
    },

    saleUrl() {
      let saleUrl = this.pendingSale.url;

      if (this.isEbaySale) {
        saleUrl = `${saleUrl}?nordt=true&rt=nc`;
      }

      return saleUrl;
    },

    terapeakLink() {
      return getTerapeakLink(this.pendingSale.title, this.pendingSale.isInternationalListing);
    },

    cardThumbnail() {
      return toImageUrl(`cards/thumb_${this.pendingSale.cardId}`);
    },

    otherGradeOptions() {
      if (!this.otherGrades || this.otherGrades.length === 0) {
        return [];
      }

      return sortOtherGrades(this.otherGrades).map((otherGrade) => ({
        value: otherGrade.id,
        label: otherGrade.condition,
      }));
    },

    otherVariationOptions() {
      if (!this.otherVariations || this.otherVariations.length === 0) {
        return [];
      }

      return this.otherVariations.map((otherVariation) => ({
        value: otherVariation.id,
        label: otherVariation.variation,
      }));
    },

    showVariationsDropdown() {
      return this.variation && this.variation.toLowerCase().includes('coating') && this.otherVariationOptions.length;
    },

    attributes() {
      const attrs = [];

      if (this.pendingSale.allTimeHigh) {
        attrs.push('All-time high');
      }

      if (this.pendingSale.memberVerified) {
        attrs.push('Member Verified Sale');
      }

      if (this.pendingSale.isInternationalListing) {
        attrs.push('International Listing');
      }

      return attrs.join(', ');
    },
  },

  methods: {
    visibilityChanged(isVisible) {
      this.visible = isVisible;

      if (isVisible && !this.loaded) {
        this.loaded = true;
        this.load();
      }
    },

    async load() {
      if (!this.sale || !this.sale.cardId) {
        return;
      }

      try {
        const ladderCard = await cardService.getCard(this.sale.cardId);

        this.pendingSale = {
          ...this.pendingSale,
          marketValue: this.pendingSale.marketValue || (ladderCard.stats && ladderCard.stats.marketValue ? ladderCard.stats.marketValue : null),
          eBayQuery: ladderCard.ebayQuery,
          newestSaleDate: ladderCard.stats?.newestSale?.date ? ladderCard.stats.newestSale.date : null,
        };

        const response = getSalesGraphData(ladderCard, 92);

        this.labels = response.labels;
        this.datasets = response.datasets;
        this.otherGrades = ladderCard.otherGrades || [];
        this.otherVariations = ladderCard.otherVariations || [];
        this.condition = ladderCard.condition || null;
        this.variation = ladderCard.variation || null;
      } catch (e) {
        Sentry.captureException(e);
      }
    },

    editPendingSale() {
      this.$bus.$emit('pending-sale-edit', this.pendingSale);
    },

    async confirmPendingSale() {
      try {
        this.$emit('remove', this.pendingSale.itemId);
        await saleService.confirmSale(this.pendingSale);
      } catch (e) {
        this.$toasted.error('Error confirming the pending sale');
      }
    },

    async sendToPurgatory() {
      try {
        this.$emit('purgatory', this.pendingSale);
        await saleService.movePendingSaleToPurgatory(this.pendingSale.itemId);

        const log = {
          cardId: this.pendingSale.cardId,
          cardEmployee: this.pendingSale.employee,
          label: this.pendingSale.cardLabel,
          uid: this.user.uid,
          type: 'PENDING_SALE_MOVED_TO_PURGATORY',
        };

        await firebase.functions().httpsCallable('httpSaveAuditLog')({ log });

        this.$toasted.success('Pending sale moved to purgatory');
      } catch (e) {
        this.$toasted.error('Error sending the pending sale to purgatory');
      }
    },

    async deletePendingSale() {
      try {
        this.$emit('remove', this.pendingSale.itemId);
        await saleService.deletePendingSale(this.pendingSale);

        const log = {
          cardId: this.pendingSale.cardId,
          cardEmployee: this.pendingSale.employee,
          label: this.pendingSale.cardLabel,
          uid: this.user.uid,
          type: 'PENDING_SALE_DELETED',
        };

        await firebase.functions().httpsCallable('httpSaveAuditLog')({ log });

        this.$toasted.success('Pending sale deleted');
      } catch (e) {
        this.$toasted.error('Error deleting the pending sale');
      }
    },

    async flagForHeadlines() {
      try {
        await saleService.flagForHeadlines(this.pendingSale);
        this.confirmPendingSale();
        this.$toasted.success('Sale is added to possible headlines and confirmed');
      } catch (e) {
        this.$toasted.error('Error flagging the sale');
      }
    },

    async switchBack() {
      if (this.pendingSale.previousCardId && window.confirm('Are you sure you want to switch this card back from the switcharoo?')) {
        await this.switchCard(this.pendingSale.previousCardId);
      }
    },

    async switchCard(cardId) {
      try {
        const ladderCard = await cardService.getCard(cardId);

        const updatedPendingSale = {
          ...this.pendingSale,
          cardId,
          cardImage: ladderCard.image,
          cardLabel: ladderCard.label,
          condition: ladderCard.condition,
          label: `${ladderCard.player} ${ladderCard.year} ${ladderCard.set} ${ladderCard.variation || ''} ${ladderCard.condition}`,
          marketValue: ladderCard.stats?.marketValue || null,
          guideValue: ladderCard.stats?.guideValue || null,
          eBayQuery: ladderCard.ebayQuery || null,
          newestSaleDate: ladderCard.newestSaleDate || null,
        };

        this.pendingSale = updatedPendingSale;
        this.$bus.$emit('pending-sale-updated', updatedPendingSale);

        const response = getSalesGraphData(ladderCard, 92);

        this.otherGradesMenuOpen = false;
        this.otherVariationsMenuOpen = false;
        this.labels = response.labels;
        this.datasets = response.datasets;
        this.otherGrades = ladderCard.otherGrades || [];
        this.otherVariations = ladderCard.otherVariations || [];
        this.condition = ladderCard.condition || null;
        this.variation = ladderCard.variation || null;
      } catch (e) {
        this.$toasted.error('Error updating the pending sale');
      }
    },

    sendEmail() {
      const body = `We received your private sale request for the following: %0D%0A%0D%0A${this.pendingSale.cardLabel} %0D%0A%0D%0AWe need some more detail before we can admit it into our database:%0D%0A%0D%0APlease respond to this email when you get a chance%0D%0AThe Card Ladder Team`;

      window.open(`mailto:${this.pendingSale.email}?subject=Card Ladder Private Sale Request&body=${body}`);
    },
  },
};
</script>

<style lang="scss" scoped>
.pending-sale {
  --text-input-margin-bottom: 0;

  padding: $gp;
  background-color: var(--theme-card);
  box-shadow: $elevation-1;
  border-radius: $border-radius;
  display: flex;
  height: 840px;
  min-height: 840px;
  max-height: 840px;

  &.alert {
    background-color: rgba($color-red-1, 0.25);
  }

  &.warning {
    background-color: rgba($color-yellow-1, 0.25);
  }

  @media($medium-down) {
    border-radius: 0;
  }
}

.wrapper {
  display: flex;
  flex: 1;
  flex-direction: column;

  &:not(.visible) {
    display: none;
  }
}

.pending-sales-graph {
  margin-top: $gp;
}

.top {
  flex: 1;
  margin-bottom: $gp;
}

.header {
  margin-bottom: $gp;
}

.images-container {
  min-width: 120px;
  max-width: 160px;
  margin-right: $gp;
}

.platform-image {
  min-height: 100px;
  width: auto;
}

.details-container {
  flex: 1;
}

p,
.title {
  margin-bottom: $gp;
  display: block;
}

form {
  margin: 0;

  .currency-input {
    max-width: 200px;
  }
}

.pending-sale-notes,
.pending-sale-employee-notes {
  margin-top: $gp;
}

.pending-sale-employee-note {
  margin-bottom: $gp * .5;
}
</style>
